import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef
} from 'react'
import noop from '@/utils/noop'

interface AutocompleteContext {
  activeList: string
  increaseLocationsLimit?: boolean
}

interface AutocompleteInstance {
  destroy: () => void
  setQuery: (query: string) => void
  setIsOpen: (isOpen: boolean) => void
  setContext: (context: Partial<AutocompleteContext>) => void
  update: () => void
}

interface SearchAutocompleteContextType {
  isOpen: boolean
  openSearch: () => void
  closeSearch: () => void
  toggleSearch: () => void
  // Instances Management
  registerInstance: (id: string, instance: AutocompleteInstance) => void
  unregisterInstance: (id: string) => void
  closeAllOtherInstances: (currentId: string) => void
  getActiveInstanceId: () => string | null
  openSpecificInstance: (preferredInstanceId?: string) => void
}

const SearchAutocompleteContext = createContext<SearchAutocompleteContextType>({
  isOpen: false,
  openSearch: noop,
  closeSearch: noop,
  toggleSearch: noop,
  registerInstance: noop,
  unregisterInstance: noop,
  closeAllOtherInstances: noop,
  getActiveInstanceId: () => null,
  openSpecificInstance: noop
})

export const useSearchAutocomplete = () => useContext(SearchAutocompleteContext)

interface SearchAutocompleteProviderProps {
  children: ReactNode
}

export const SearchAutocompleteProvider: React.FC<
  SearchAutocompleteProviderProps
> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const activeInstanceIdRef = useRef<string | null>(null)
  const registryRef = useRef<Map<string, AutocompleteInstance>>(new Map())

  const closeSearch = useCallback(() => {
    setIsOpen(false)
    activeInstanceIdRef.current = null
  }, [])

  const toggleSearch = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const registerInstance = useCallback(
    (id: string, instance: AutocompleteInstance) => {
      registryRef.current.set(id, instance)
    },
    []
  )

  const unregisterInstance = useCallback((id: string) => {
    const instance = registryRef.current.get(id)
    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy()
      } catch (error) {
        console.warn(`Failed to destroy autocomplete instance ${id}:`, error)
      }
    }
    registryRef.current.delete(id)
    if (activeInstanceIdRef.current === id) {
      activeInstanceIdRef.current = null
    }
  }, [])

  const closeAllOtherInstances = useCallback((currentId: string) => {
    Array.from(registryRef.current.entries()).forEach(([id, instance]) => {
      if (
        id !== currentId &&
        instance &&
        typeof instance.setIsOpen === 'function'
      ) {
        try {
          instance.setIsOpen(false)
        } catch (error) {
          console.warn(`Failed to close autocomplete instance ${id}:`, error)
        }
      }
    })
    activeInstanceIdRef.current = currentId
  }, [])

  const openSearch = useCallback(() => {
    setIsOpen(true)
  }, [])

  const getActiveInstanceId = useCallback(() => {
    return activeInstanceIdRef.current
  }, [])

  const openSpecificInstance = useCallback((preferredInstanceId?: string) => {
    setIsOpen(true)
  }, [])

  const value = {
    isOpen,
    openSearch,
    closeSearch,
    toggleSearch,
    registerInstance,
    unregisterInstance,
    closeAllOtherInstances,
    getActiveInstanceId,
    openSpecificInstance
  }

  return (
    <SearchAutocompleteContext.Provider value={value}>
      {children}
    </SearchAutocompleteContext.Provider>
  )
}
